// Nine Lives '84 - the game.
//
// Owns Tabs, the alley, the current room, the score and lives, and
// advances one tick per call. Scenes: the alley, and a room entered through
// an open window. No DOM, no three.js.

import { Alley, type AlleyWindow } from './alley';
import { newCat, updateCat, type Cat } from './cat';
import { CAT_W, CAUGHT_TICKS, FREEZE_TICKS, INVULN_TICKS, WORLD_W } from './constants';
import { difficultyParams, type DifficultyParams } from './difficulty';
import { Rng } from './rng';
import type { Room } from './room';
import { BirdcageRoom } from './rooms/birdcage';
import { BookshelfRoom } from './rooms/bookshelf';
import { CheeseRoom } from './rooms/cheese';
import { DogsRoom } from './rooms/dogs';
import { FeliciaRoom } from './rooms/felicia';
import { FishbowlRoom } from './rooms/fishbowl';
import { Difficulty, type GameEvent, type Platform, type RoomKind } from './types';

export interface GameConfig {
  difficulty: Difficulty;
  seed: number;
  /** Hold the simulation briefly at scene changes so the view can dolly through. */
  transitions: boolean;
}

export type Scene = 'alley' | 'room';

export const ROOM_KINDS: RoomKind[] = ['cheese', 'bookshelf', 'birdcage', 'dogs', 'fishbowl'];

export class Game {
  readonly config: GameConfig;
  readonly rng: Rng;
  readonly cat: Cat;
  readonly alley: Alley;
  params: DifficultyParams;
  scene: Scene = 'alley';
  room: Room | null = null;
  /** Window Tabs went in through, while in a room. */
  roomWindow: AlleyWindow | null = null;
  /** Force the next room (tests, ?room=). */
  nextRoom: RoomKind | null = null;
  score = 0;
  lives: number;
  level = 1;
  /** Rooms completed this level. */
  roomsDone = 0;
  /** Ticks since the game started. */
  count = 0;
  isOver = false;
  /** A short prompt for the HUD ("X: climb in"), or empty. */
  hint = '';
  /** Ticks left holding still for a scene transition; the view dollies meanwhile. */
  freeze = 0;
  private caughtTicks = 0;
  private events: GameEvent[] = [];
  /** Rooms not yet visited this level; refilled when empty so all five come round. */
  private roomBag: RoomKind[] = [];

  constructor(config: Partial<GameConfig> = {}) {
    this.config = {
      difficulty: config.difficulty ?? Difficulty.HOUSE_CAT,
      seed: config.seed ?? 1,
      transitions: config.transitions ?? true,
    };
    this.rng = new Rng(this.config.seed);
    this.params = difficultyParams(this.config.difficulty, 1);
    this.lives = this.params.lives;
    this.alley = new Alley(this.rng, this.params);
    this.cat = newCat(WORLD_W / 2 - CAT_W / 2, 0);
  }

  /** The platforms of whatever scene Tabs is in. */
  get platforms(): Platform[] {
    return this.room ? this.room.platforms : this.alley.platforms;
  }

  tick(keys: number): void {
    if (this.isOver) return;
    this.count++;
    if (this.freeze > 0) {
      this.freeze--;
      return;
    }
    this.hint = '';

    if (this.room) {
      this.tickRoom(keys);
      return;
    }

    if (this.cat.state === 'caught') {
      if (--this.caughtTicks <= 0) this.loseLife();
      return;
    }

    updateCat(this.cat, keys, this.platforms, this.events);
    const win = this.alley.update(this.cat, keys, this.events);
    if (win) {
      this.enterRoom(win);
      return;
    }
    if (this.alley.dogCatches(this.cat)) {
      this.cat.state = 'caught';
      this.cat.vx = 0;
      this.cat.vy = 0;
      this.alley.dog.biting = true;
      this.caughtTicks = CAUGHT_TICKS;
      this.events.push({ kind: 'caught' });
      return;
    }
    const sill = this.alley.sillUnder(this.cat);
    if (sill?.open) this.hint = sill.felicia ? 'X: go to Clementine' : 'X: climb in';
  }

  private tickRoom(keys: number): void {
    const r = this.room!;
    if (this.cat.state === 'caught') {
      if (--this.caughtTicks <= 0) {
        this.returnToAlley();
        this.loseLife();
      }
      return;
    }
    r.step(this.cat, keys, this.events);
    if (r.freezeRequest) {
      this.hold(r.freezeRequest);
      r.freezeRequest = 0;
    }
    if (r instanceof FeliciaRoom) {
      this.tickFelicia(r);
      return;
    }
    if (r.fatal) {
      this.cat.state = 'caught';
      this.cat.vx = 0;
      this.cat.vy = 0;
      this.caughtTicks = CAUGHT_TICKS;
      this.events.push({ kind: r.fatal === 'dog' ? 'caught' : 'drowned' });
      return;
    }
    if (r.done) {
      const bonus = r.bonus;
      this.score += bonus;
      this.roomsDone++;
      this.returnToAlley();
      this.alley.showFelicia();
      this.events.push({ kind: 'roomDone', bonus });
      this.events.push({ kind: 'felicia' });
    } else if (r.shooed || r.left) {
      this.returnToAlley();
    } else {
      this.hint = this.roomHint(r);
    }
  }

  private tickFelicia(r: FeliciaRoom): void {
    if (r.kissed) {
      const bonus = r.bonus;
      this.score += bonus;
      this.lives++;
      this.level++;
      this.roomsDone = 0;
      this.params = difficultyParams(this.config.difficulty, this.level);
      this.alley.params = this.params;
      this.returnToAlley();
      this.alley.hideFelicia();
      // The room raised a placeholder kiss; replace it with the real numbers.
      this.events = this.events.filter((e) => e.kind !== 'kiss');
      this.events.push({ kind: 'kiss', level: this.level, lives: this.lives, bonus });
    } else if (r.failed) {
      this.returnToAlley();
      this.alley.hideFelicia();
    } else {
      this.hint = r.held > 0 ? `${r.held} gift${r.held > 1 ? 's' : ''} to bribe a brother` : '';
    }
  }

  private roomHint(r: Room): string {
    if (this.cat.ground === r.sill) return 'X: back to the alley';
    if (r instanceof CheeseRoom && r.holeAt(this.cat)) return 'X: crawl in';
    if (r instanceof DogsRoom && r.bowlAt(this.cat)) {
      return r.dogs.some((d) => d.state === 'growl') ? 'GRRR… back off, let him settle' : '↓: drink';
    }
    if (r instanceof FishbowlRoom && r.surfaced) return 'X: climb out';
    return '';
  }

  private makeRoom(kind: RoomKind): Room {
    const ctx = { rng: this.rng, params: this.params, level: this.level };
    switch (kind) {
      case 'cheese':
        return new CheeseRoom(ctx);
      case 'bookshelf':
        return new BookshelfRoom(ctx);
      case 'birdcage':
        return new BirdcageRoom(ctx);
      case 'dogs':
        return new DogsRoom(ctx);
      case 'fishbowl':
        return new FishbowlRoom(ctx);
      case 'felicia':
        return new FeliciaRoom(ctx);
    }
  }

  /** Draw the next room kind: forced, or from a shuffled bag of all five. */
  private drawRoom(): RoomKind {
    if (this.nextRoom) {
      const k = this.nextRoom;
      this.nextRoom = null;
      return k;
    }
    if (!this.roomBag.length) this.roomBag = [...ROOM_KINDS];
    const i = this.rng.int(0, this.roomBag.length - 1);
    return this.roomBag.splice(i, 1)[0];
  }

  private enterRoom(win: AlleyWindow): void {
    const kind = win.felicia ? 'felicia' : this.drawRoom();
    this.scene = 'room';
    this.roomWindow = win;
    this.room = this.makeRoom(kind);
    this.room.spawn(this.cat);
    this.hold(FREEZE_TICKS);
    this.events.push({ kind: 'enterWindow', window: win.index });
    this.events.push({ kind: 'roomStart', room: kind });
  }

  /** Back out onto the sill Tabs came in by; the window shuts behind him. */
  private returnToAlley(): void {
    const win = this.roomWindow!;
    this.room = null;
    this.roomWindow = null;
    this.scene = 'alley';
    this.alley.closeAll();
    const c = this.cat;
    c.x = win.x + win.w / 2 - c.w / 2;
    c.y = win.sill.y + win.sill.h;
    c.vx = 0;
    c.vy = 0;
    c.onGround = true;
    c.ground = win.sill;
    c.dropTicks = 0;
    c.knockedTicks = 0;
    c.leaping = false;
    this.hold(FREEZE_TICKS);
  }

  private hold(ticks: number): void {
    if (this.config.transitions) this.freeze = ticks;
  }

  private loseLife(): void {
    this.lives--;
    if (this.lives <= 0) {
      this.lives = 0;
      this.end();
      return;
    }
    this.respawn();
  }

  private respawn(): void {
    const c = this.cat;
    c.state = 'alive';
    c.x = 12;
    c.y = 0;
    c.vx = 0;
    c.vy = 0;
    c.onGround = true;
    c.ground = null;
    c.facing = 1;
    c.invuln = INVULN_TICKS;
    c.knockedTicks = 0;
    c.dropTicks = 0;
    this.alley.sendDogAway();
    this.events.push({ kind: 'respawn' });
  }

  /** Drain the events raised since the last call (sounds, HUD messages). */
  takeEvents(): GameEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  end(): void {
    if (this.isOver) return;
    this.isOver = true;
    this.events.push({ kind: 'gameOver' });
  }
}
